import React, { useMemo } from "react";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";
import { Field } from "formik";
import { SimpleSingleSelect } from "../../../components/FormComponents/SimpleSingleSelect";
import moment from "moment";
import { getCustomFormattedNameFromObject } from "../../../services/stringService";
import IncomeFields from "../Components/IncomeFields";

function getAge(dob) {
    const dateOfBirth = dob ? moment(dob) : moment();
    const age = moment().diff(dateOfBirth.format("LL"), "years");
    return age;
}

function getFullName(member, showRelationship = false) {
    return getCustomFormattedNameFromObject(member.demographic, "", showRelationship);
}

const InterviewPersonInfoScreeningForm = ({
    values,
    setFieldValue,
    allHouseholdMembers,
    getErrors = () => {},
    getValidationState = () => {},
    setFieldTouched,
    states,
    incomeType,
    incomeFrequency,
    maxDependeeAge,
    acceptedIncomeProofs,
    documentTypes,
    patientPersonId,
    isRequired,
    responsiblePartyPersonId,
    ...props
}) => {
    if (isRequired == null) {
        isRequired = () => false;
    }

    return (
        <>
            {values.householdMembers.map((member, i) => (
                <PersonForm
                    key={i}
                    values={values}
                    setFieldValue={setFieldValue}
                    allHouseholdMembers={allHouseholdMembers}
                    getErrors={getErrors}
                    getValidationState={getValidationState}
                    setFieldTouched={setFieldTouched}
                    states={states}
                    incomeType={incomeType}
                    incomeFrequency={incomeFrequency}
                    maxDependeeAge={maxDependeeAge}
                    acceptedIncomeProofs={acceptedIncomeProofs}
                    documentTypes={documentTypes}
                    patientPersonId={patientPersonId}
                    isRequired={isRequired}
                    member={member}
                    responsiblePartyPersonId={responsiblePartyPersonId}
                    i={i}
                    {...props}
                />
            ))}
        </>
    );
};

const PersonForm = ({
    values,
    setFieldValue,
    allHouseholdMembers,
    getErrors = () => {},
    getValidationState = () => {},
    setFieldTouched,
    states,
    incomeType,
    incomeFrequency,
    maxDependeeAge,
    acceptedIncomeProofs,
    documentTypes,
    patientPersonId,
    responsiblePartyPersonId,
    isRequired,
    member,
    i,
    ...props
}) => {
    const fullName = getFullName(member, false);
    const fullNameWithRelationship = getFullName(member, true);

    const potentialPeopleToFileJointlyWith = useMemo(() => {
        let allowableHouseholdMembers = [];

        let patientSpouseMemId = allHouseholdMembers.filter(x => x.demographic.relationshipToPatient === "Spouse")[0]
            ?.personId;
        let responsibleSpouseMemId = allHouseholdMembers.filter(
            x => x.demographic.relationshipToResponsibleParty === "Spouse"
        )[0]?.personId;

        if (member.personId === patientPersonId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(
                x => x.demographic.relationshipToPatient === "Spouse"
            );
        } else if (member.personId === responsiblePartyPersonId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(
                x => x.demographic.relationshipToResponsibleParty === "Spouse"
            );
        } else if (member.personId === patientSpouseMemId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(x => x.personId === patientPersonId);
            // Return patient here.
        } else if (member.personId === responsibleSpouseMemId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(x => x.personId === responsiblePartyPersonId);
        } else {
            allowableHouseholdMembers = allHouseholdMembers;

            if (patientSpouseMemId != null) {
                allowableHouseholdMembers = allHouseholdMembers.filter(
                    x => x.personId !== patientPersonId && x.personId !== patientSpouseMemId
                );
            }

            if (responsibleSpouseMemId != null) {
                allowableHouseholdMembers = allowableHouseholdMembers.filter(
                    x => x.personId !== responsiblePartyPersonId && x.personId !== responsibleSpouseMemId
                );
            }
        }

        return allowableHouseholdMembers
            .map(x => ({
                label: x.demographic.firstName,
                value: x.personId,
            }))
            .filter(x => x.value !== values.householdMembers[i].personId);
    }, [allHouseholdMembers]);

    const potentialDependees = useMemo(() => {
        let allowableHouseholdMembers = [];
        let patientSpouseMemid = allHouseholdMembers.filter(x => x.demographic.relationshipToPatient === "Spouse")[0]
            ?.personId;
        let responsibleSpouseMemid = allHouseholdMembers.filter(
            x => x.demographic.relationshipToResponsibleParty === "Spouse"
        )[0]?.personId;

        if (member.personId === patientPersonId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(
                x =>
                    x.demographic.relationshipToPatient !== "Unrelated" &&
                    x.demographic.relationshipToPatient !== "Spouse"
            );
        } else if (member.personId === responsiblePartyPersonId) {
            allowableHouseholdMembers = allHouseholdMembers.filter(
                x =>
                    x.demographic.relationshipToResponsibleParty !== "Unrelated" &&
                    x.demographic.relationshipToResponsibleParty !== "Spouse"
            );
        } else {
            allowableHouseholdMembers = allHouseholdMembers;
        }

        return allowableHouseholdMembers
            .map(x => ({
                label: x.demographic.firstName,
                value: x.personId,
            }))
            .filter(x => x.value !== values.householdMembers[i].personId);
    }, [allHouseholdMembers]);

    function shouldShowIsDisabled() {
        if (values.householdMembers[i].personInfoScreening.willBeClaimedAsDependent) {
            if (getAge(values.householdMembers[i].demographic.dob) >= maxDependeeAge) {
                return true;
            }
        }

        return false;
    }

    return (
        <div
            key={i}
            className="household-details-member"
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
        >
            <h4 className="person-name">{fullNameWithRelationship}</h4>
            <div className="form-section">
                <div className="form-control xxl-input">
                    <YesNoButtons
                        {...props}
                        name={`householdMembers.${i}.personInfoScreening.planToFileTaxes`}
                        title={`Does ${fullName} plan to file taxes this year?`}
                        setFieldValue={setFieldValue}
                        getErrors={getErrors}
                        isRequired={isRequired}
                        getValidationState={getValidationState}
                    />
                </div>

                {values.householdMembers[i].personInfoScreening.planToFileTaxes &&
                    values.householdMembers[i].demographic.maritalStatus !== "Single" && (
                        <div className="form-control xxl-input">
                            <YesNoButtons
                                {...props}
                                name={`householdMembers.${i}.personInfoScreening.willFileJointly`}
                                setFieldValue={setFieldValue}
                                title={`Will ${fullName} file jointly?`}
                                getErrors={getErrors}
                                getValidationState={getValidationState}
                                isRequired={() => true}
                                onFieldChange={value => {
                                    if (value === true) {
                                        setFieldValue(
                                            `householdMembers.${i}.personInfoScreening.willBeClaimedAsDependent`,
                                            false
                                        );
                                        setFieldValue(
                                            `householdMembers.${i}.personInfoScreening.claimedAsADependentBy`,
                                            null
                                        );
                                    } else if (value === false) {
                                        setFieldValue(
                                            `householdMembers.${i}.personInfoScreening.filesJointlyWith`,
                                            null
                                        );
                                    }
                                }}
                            />
                        </div>
                    )}

                {values.householdMembers[i].personInfoScreening.planToFileTaxes &&
                    values.householdMembers[i].personInfoScreening.willFileJointly && (
                        <div className="form-control l-input">
                            <Field
                                name={`householdMembers.${i}.personInfoScreening.filesJointlyWith`}
                                render={({ field }) => (
                                    <SimpleSingleSelect
                                        {...field}
                                        title={`Who will ${fullName} file jointly with?`}
                                        id={`householdMembers.${i}.personInfoScreening.filesJointlyWith`}
                                        validState={getValidationState(field)}
                                        setFieldTouched={setFieldTouched}
                                        setFieldValue={setFieldValue}
                                        errorMessage={getErrors(field)}
                                        required={isRequired(field)}
                                        options={potentialPeopleToFileJointlyWith}
                                        onFieldChange={value => {
                                            let matchingPersonIndex;
                                            for (let j = 0; j < values.householdMembers.length; j++) {
                                                const currentPerson = values.householdMembers[j].personId;
                                                if (currentPerson === value) {
                                                    matchingPersonIndex = j;
                                                    break;
                                                }
                                            }

                                            setFieldValue(
                                                `householdMembers.${matchingPersonIndex}.personInfoScreening.planToFileTaxes`,
                                                true
                                            );
                                            setFieldValue(
                                                `householdMembers.${matchingPersonIndex}.personInfoScreening.filesJointlyWith`,
                                                member.personId
                                            );
                                            setFieldValue(
                                                `householdMembers.${matchingPersonIndex}.personInfoScreening.willFileJointly`,
                                                true
                                            );
                                        }}
                                    />
                                )}
                            />
                        </div>
                    )}
            </div>

            {!values.householdMembers[i].personInfoScreening.willFileJointly && (
                <div className="form-section">
                    <div className="form-control xxl-input">
                        <YesNoButtons
                            {...props}
                            name={`householdMembers.${i}.personInfoScreening.willBeClaimedAsDependent`}
                            title={`Will ${fullName} be claimed as a tax dependent on a tax return?`}
                            setFieldValue={setFieldValue}
                            getErrors={getErrors}
                            isRequired={() => true}
                            getValidationState={getValidationState}
                        />
                    </div>

                    {values.householdMembers[i].personInfoScreening.willBeClaimedAsDependent && (
                        <div className="form-control l-input">
                            <Field
                                name={`householdMembers.${i}.personInfoScreening.claimedAsADependentBy`}
                                render={({ field }) => (
                                    <SimpleSingleSelect
                                        {...field}
                                        title={`Who will ${fullName} be claimed as dependent by?`}
                                        id={`householdMembers.${i}.personInfoScreening.claimedAsADependentBy`}
                                        validState={getValidationState(field)}
                                        setFieldTouched={setFieldTouched}
                                        setFieldValue={setFieldValue}
                                        errorMessage={getErrors(field)}
                                        required={isRequired(field)}
                                        options={potentialDependees}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="form-section">
                {shouldShowIsDisabled() && (
                    <div className="form-control xxl-input">
                        <YesNoButtons
                            {...props}
                            name={`householdMembers.${i}.personInfoScreening.isDisabled`}
                            title={`Is ${fullName} disabled?`}
                            setFieldValue={setFieldValue}
                            getValidationState={getValidationState}
                            isRequired={() => true}
                            getErrors={getErrors}
                        />
                    </div>
                )}
            </div>
            <IncomeFields
                personField={`householdMembers.${i}.personInfoScreening`}
                getValidationState={getValidationState}
                isRequired={isRequired}
                getErrors={getErrors}
                incomeFrequency={incomeFrequency}
                incomeTypes={incomeType}
                personName={fullName}
            />
        </div>
    );
};

export default InterviewPersonInfoScreeningForm;
